import type {
  FlightChoice,
  FlightOffer,
  FlightOfferFare,
  FlightOfferSlice,
} from '../chat/sidebar/flightArtifact';
import { bagsLabel } from '../chat/sidebar/flightArtifact';
import type { HotelChoice, HotelOffer } from '../chat/sidebar/hotelArtifact';

export type OptionGroup = 'transport' | 'stays';

export interface Segment {
  carrierCode: string;
  carrierName: string;
  number: string;
  from: string;
  fromName?: string;
  to: string;
  toName?: string;
  fromTerminal?: string;
  toTerminal?: string;
  aircraft?: string;
  operatedBy?: string;
  dep: string;
  arr: string;
  durationMin: number;
}

export interface Leg {
  label: string;
  from: string;
  to: string;
  dep: string;
  arr: string;
  durationMin: number;
  segments: Segment[];
}

export interface Fare {
  id: string;
  name: string;
  nameDetail?: string;
  total: number;
  currency: string;
  bags: string | null;
  conditions: string;
  refundable: boolean | null;
}

export interface TransportOption {
  kind: 'flight' | 'train';
  id: string;
  artifactId: string;
  title: string;
  mode: 'instant' | 'select' | 'agent';
  legs: Leg[];
  fares: Fare[];
  price: number;
  currency: string;
  passengers: number;
  returnDate?: string | null;
  returnOptions?: number | null;
  pairedSearch?: boolean;
  returnAlternatives?: number | null;
  blurb?: string;
}

export interface StayRate {
  id: string;
  room: string;
  board: string;
  refundable: boolean;
  cancellation: string;
  total: number;
  perNight: number | null;
  currency: string;
  freeUntil: string | null;
}

export interface StayRoom {
  name: string;
  description: string | null;
  beds: string | null;
  sizeSqm: number | null;
  sleeps: number | null;
  photo: string | null;
  amenities: string[];
  /** Positions in StayOption.rates. */
  rates: number[];
}

export interface StayOption {
  kind: 'hotel';
  id: string;
  artifactId: string;
  title: string;
  mode: 'instant' | 'agent';
  stars: number | null;
  rating: { score: number; outOf: number; count?: number | null } | null;
  photos: string[];
  address: string | null;
  checkIn: string;
  checkOut: string;
  nights: number;
  adults: number;
  children: number;
  guests: number;
  rates: StayRate[];
  rooms: StayRoom[];
  description: string | null;
  price: number;
  currency: string;
  facilities: string[];
  blurb?: string;
  source: HotelChoice;
}

export type TravelOption = TransportOption | StayOption;

export interface ReturnFlight {
  id: string;
  dep: string;
  arr: string;
  from: string;
  to: string;
  airlines: string;
  durationMin: number;
  stops: number;
  price: number;
  currency: string;
  leg: Leg;
  fares?: Fare[];
  separateTickets?: boolean;
  choiceId?: string;
  note?: { headline: string; detail?: string };
  sourcePrices?: Array<{ total: number; currency: string }>;
}

export interface LegFare {
  id: string;
  name: string;
  nameDetail?: string;
  price: number;
  currency: string;
  bags: string | null;
  conditions: string;
}

const minutes = (a: string, b: string) =>
  Math.round(
    (new Date(`${b}Z`).getTime() - new Date(`${a}Z`).getTime()) / 60000,
  );

type RecordValue = Record<string, unknown>;

function isRecord(value: unknown): value is RecordValue {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

function isNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function warnInvalidOffer(choiceId: string, kind: string) {
  console.warn(
    `[travelOptions] Skipping ${kind} choice ${choiceId}: invalid offer payload`,
  );
}

function isFlightOffer(value: unknown): value is FlightOffer {
  if (!isRecord(value)) return false;
  if (
    !['instant', 'select', 'agent'].includes(String(value.bookingMode)) ||
    !isNumber(value.passengerCount) ||
    !Array.isArray(value.slices) ||
    value.slices.length === 0
  ) {
    return false;
  }
  const slicesValid = value.slices.every((slice) => {
    if (
      !isRecord(slice) ||
      !isString(slice.origin) ||
      !isString(slice.destination) ||
      !isString(slice.departingAt) ||
      !isString(slice.arrivingAt) ||
      !Array.isArray(slice.segments) ||
      slice.segments.length === 0
    ) {
      return false;
    }
    return slice.segments.every(
      (segment) =>
        isRecord(segment) &&
        isString(segment.carrier) &&
        isString(segment.carrierName) &&
        isString(segment.flightNumber) &&
        isString(segment.origin) &&
        isString(segment.destination) &&
        isString(segment.departingAt) &&
        isString(segment.arrivingAt),
    );
  });
  const faresValid =
    value.fares === undefined ||
    (Array.isArray(value.fares) &&
      value.fares.every(
        (fare) =>
          isRecord(fare) &&
          isString(fare.offerId) &&
          isNumber(fare.total) &&
          isString(fare.currency) &&
          typeof fare.conditions === 'string',
      ));
  const sourcesValid =
    value.sources === undefined ||
    (Array.isArray(value.sources) &&
      value.sources.every(
        (source) =>
          isRecord(source) &&
          isString(source.name) &&
          isString(source.currency) &&
          (source.total == null || isNumber(source.total)),
      ));
  return slicesValid && faresValid && sourcesValid;
}

function isHotelOffer(value: unknown): value is HotelOffer {
  if (!isRecord(value) || !isRecord(value.stay)) return false;
  const stay = value.stay;
  const roomsValid =
    value.rooms === undefined ||
    (Array.isArray(value.rooms) &&
      value.rooms.every(
        (room) =>
          isRecord(room) &&
          isString(room.name) &&
          Array.isArray(room.rates) &&
          room.rates.every(
            (rate) =>
              isRecord(rate) &&
              isString(rate.offerId) &&
              isString(rate.board) &&
              typeof rate.refundable === 'boolean' &&
              typeof rate.cancellationTerms === 'string' &&
              isNumber(rate.total) &&
              isString(rate.currency),
          ),
      ));
  const sourcesValid =
    value.sources === undefined ||
    (Array.isArray(value.sources) &&
      value.sources.every(
        (source) =>
          isRecord(source) &&
          isString(source.name) &&
          isString(source.currency) &&
          (source.total == null || isNumber(source.total)),
      ));
  return (
    ['instant', 'agent'].includes(String(value.bookingMode)) &&
    isString(stay.checkInDate) &&
    isString(stay.checkOutDate) &&
    isNumber(stay.nights) &&
    isNumber(stay.adults) &&
    isNumber(stay.children) &&
    roomsValid &&
    sourcesValid &&
    (value.photos === undefined ||
      (Array.isArray(value.photos) && value.photos.every(isString)))
  );
}

function legFromSlice(
  slice: FlightOfferSlice,
  index: number,
  count: number,
): Leg {
  return {
    label: count === 1 ? 'Outbound' : index === 0 ? 'Outbound' : 'Return',
    from: slice.origin,
    to: slice.destination,
    dep: slice.departingAt,
    arr: slice.arrivingAt,
    durationMin:
      slice.durationMinutes ?? minutes(slice.departingAt, slice.arrivingAt),
    segments: slice.segments.map((segment) => ({
      carrierCode: segment.carrier,
      carrierName: segment.carrierName,
      number: `${segment.carrier} ${segment.flightNumber}`,
      from: segment.origin,
      to: segment.destination,
      fromTerminal: segment.originTerminal ?? undefined,
      toTerminal: segment.destinationTerminal ?? undefined,
      aircraft: segment.aircraft ?? undefined,
      operatedBy: segment.operatedBy ?? undefined,
      dep: segment.departingAt,
      arr: segment.arrivingAt,
      durationMin:
        segment.durationMinutes ??
        minutes(segment.departingAt, segment.arrivingAt),
    })),
  };
}

export function capitalizeConditionSegments(conditions: string): string {
  return conditions
    .split(' · ')
    .map((segment) =>
      segment ? `${segment[0].toUpperCase()}${segment.slice(1)}` : segment,
    )
    .join('. ');
}

function travelBagsLabel(fare: FlightOfferFare): string | null {
  const label = bagsLabel(fare);
  if (label === null) return null;
  return label
    .split(' · ')
    .map((part, index) =>
      index === 0 ? part : `${part.slice(0, 1).toLowerCase()}${part.slice(1)}`,
    )
    .join(', ');
}

export function adaptFlightChoice(
  artifactId: string,
  choice: FlightChoice,
): TransportOption | null {
  const offerValue: unknown = choice.flightOffer;
  if (!isFlightOffer(offerValue)) {
    warnInvalidOffer(choice.choiceId, 'flight');
    return null;
  }
  const offer = offerValue;
  const fares: Fare[] = (offer.fares ?? []).map((fare) => ({
    id: fare.offerId,
    name: fare.brandName || fare.cabin,
    total: fare.total,
    currency: fare.currency,
    bags: travelBagsLabel(fare),
    conditions: capitalizeConditionSegments(fare.conditions),
    refundable: fare.refundable,
  }));
  const source = offer.sources?.[0];
  if (fares.length === 0) {
    fares.push({
      id: choice.choiceId,
      name: 'Standard',
      total: choice.fromPrice ?? source?.total ?? 0,
      currency: source?.currency ?? 'GBP',
      bags: null,
      conditions: '',
      refundable: null,
    });
  }
  const mode =
    offer.slices.length === 1 && isString(offer.departureTokenId)
      ? 'select'
      : offer.bookingMode;
  return {
    kind: 'flight',
    id: choice.choiceId,
    artifactId,
    title: choice.choiceTitle ?? 'Flight',
    mode,
    legs: offer.slices.map((slice, index) =>
      legFromSlice(slice, index, offer.slices.length),
    ),
    fares,
    price:
      mode === 'select'
        ? (choice.fromPrice ?? source?.total ?? fares[0]?.total ?? 0)
        : (fares[0]?.total ?? choice.fromPrice ?? source?.total ?? 0),
    currency:
      (mode === 'select' ? source?.currency : undefined) ??
      fares[0]?.currency ??
      source?.currency ??
      'GBP',
    passengers: offer.passengerCount,
    returnDate:
      typeof offer.returnDate === 'string' ? offer.returnDate : undefined,
    returnOptions: offer.returnOptions,
    pairedSearch: offer.pairedSearch,
    returnAlternatives: offer.returnAlternatives,
    blurb: choice.choiceBlurb,
  };
}

export function adaptHotelChoice(
  artifactId: string,
  choice: HotelChoice,
): StayOption | null {
  const hotelValue: unknown = choice.hotelOffer;
  if (!isHotelOffer(hotelValue)) {
    warnInvalidOffer(choice.choiceId, 'hotel');
    return null;
  }
  const hotel = hotelValue;
  const rates: StayRate[] = [];
  const rooms: StayRoom[] = (hotel.rooms ?? []).map((room) => ({
    name: room.name,
    description: room.description ?? null,
    beds: room.beds ?? null,
    sizeSqm: room.sizeSqm ?? null,
    sleeps: room.sleeps ?? null,
    photo: room.photo ?? room.photos?.[0] ?? null,
    amenities: room.amenities ?? [],
    rates: room.rates.map((rate) => {
      rates.push({
        id: rate.offerId,
        room: room.name,
        board: rate.board,
        refundable: rate.refundable,
        cancellation: rate.cancellationTerms,
        total: rate.total,
        perNight: rate.perNight ?? null,
        currency: rate.currency,
        freeUntil: rate.freeCancellationUntil ?? null,
      });
      return rates.length - 1;
    }),
  }));
  const firstRate = rates[0];
  return {
    kind: 'hotel',
    id: choice.choiceId,
    artifactId,
    title: choice.choiceTitle,
    mode: hotel.bookingMode,
    stars: hotel.stars ?? null,
    rating: hotel.rating
      ? {
          score: hotel.rating.score,
          outOf: hotel.rating.outOf,
          count: hotel.rating.reviewCount,
        }
      : null,
    photos:
      hotel.photos ?? hotel.rooms?.flatMap((room) => room.photos ?? []) ?? [],
    address: hotel.address ?? null,
    checkIn: hotel.stay.checkInDate,
    checkOut: hotel.stay.checkOutDate,
    nights: hotel.stay.nights,
    adults: hotel.stay.adults,
    children: hotel.stay.children,
    guests: hotel.stay.adults + hotel.stay.children,
    rates,
    rooms,
    description: hotel.description?.trim() || null,
    price:
      firstRate?.total ?? choice.fromPrice ?? hotel.sources?.[0]?.total ?? 0,
    currency: firstRate?.currency ?? hotel.sources?.[0]?.currency ?? 'GBP',
    facilities: hotel.facilities ?? [],
    blurb: choice.choiceBlurb,
    source: choice,
  };
}

// Mirrors railOffer fare fields from dream-be (lib/rail/artifactOffer.ts) until published in dream-types.
interface RailFare {
  offerId: string;
  price: number;
  currency: string;
  conditionsSummary: string;
  ticketType?: string | null;
  serviceClass?: string | null;
}

function isRailFare(value: unknown): value is RailFare {
  return (
    isRecord(value) &&
    isString(value.offerId) &&
    isNumber(value.price) &&
    isString(value.currency) &&
    isString(value.conditionsSummary)
  );
}

/** The chosen fare first, then the train's other fares. */
function railFares(offer: Record<string, unknown>): RailFare[] {
  if (!isRailFare(offer)) return [];
  const others = Array.isArray(offer.fares)
    ? offer.fares.filter(
        (fare): fare is RailFare =>
          isRailFare(fare) && fare.offerId !== offer.offerId,
      )
    : [];
  return [offer, ...others];
}

export function railFareName(fare: RailFare): {
  name: string;
  nameDetail?: string;
} {
  const serviceClass = fare.serviceClass?.trim();
  const ticketType = fare.ticketType?.trim();
  if (
    serviceClass &&
    ticketType &&
    ticketType.toLowerCase().includes(serviceClass.toLowerCase())
  ) {
    return { name: ticketType };
  }
  if (serviceClass && ticketType) {
    return { name: `${serviceClass}.`, nameDetail: ticketType };
  }
  return { name: serviceClass || ticketType || 'Train ticket' };
}

// Mirrors railOffer.journey from dream-be (lib/rail/artifactOffer.ts) until published in dream-types.
interface RailJourneyLeg {
  originStation: string;
  destinationStation: string;
  departureAt: string;
  arrivalAt: string;
  durationMinutes: number | null;
  operator: string | null;
  trainNumber: string | null;
}

interface RailJourney {
  originStation: string;
  destinationStation: string;
  departureAt: string;
  arrivalAt: string;
  durationMinutes: number | null;
  legs: RailJourneyLeg[];
}

function isRailJourney(value: unknown): value is RailJourney {
  return (
    isRecord(value) &&
    isString(value.originStation) &&
    isString(value.destinationStation) &&
    isString(value.departureAt) &&
    isString(value.arrivalAt) &&
    Array.isArray(value.legs) &&
    value.legs.length > 0 &&
    value.legs.every(
      (leg) =>
        isRecord(leg) &&
        isString(leg.originStation) &&
        isString(leg.destinationStation) &&
        isString(leg.departureAt) &&
        isString(leg.arrivalAt),
    )
  );
}

// Operator shown as a short text mark: "LNER" stays, "Avanti West Coast" -> "AWC".
const operatorCode = (operator: string) => {
  const words = operator.split(/\s+/).filter(Boolean);
  return words.length > 1
    ? words.map((word) => word[0].toUpperCase()).join('')
    : operator.slice(0, 4).toUpperCase();
};

function legFromJourney(journey: RailJourney): Leg {
  return {
    label: 'Outbound',
    from: journey.originStation,
    to: journey.destinationStation,
    dep: journey.departureAt,
    arr: journey.arrivalAt,
    durationMin:
      journey.durationMinutes ??
      minutes(journey.departureAt, journey.arrivalAt),
    segments: journey.legs.map((leg) => {
      const operator = leg.operator ?? 'Train';
      return {
        carrierCode: operatorCode(operator),
        carrierName: operator,
        number: leg.trainNumber ?? '',
        from: leg.originStation,
        fromName: leg.originStation,
        to: leg.destinationStation,
        toName: leg.destinationStation,
        dep: leg.departureAt,
        arr: leg.arrivalAt,
        durationMin:
          leg.durationMinutes ?? minutes(leg.departureAt, leg.arrivalAt),
      };
    }),
  };
}

export function adaptRailChoice(
  artifactId: string,
  choice: {
    choiceId: string;
    choiceTitle?: string;
    choiceBlurb?: string;
    fromPrice?: number;
    priceCurrency?: string;
    railOffer?: unknown;
  },
): TransportOption | null {
  const offer = choice.railOffer;
  if (
    !isRecord(offer) ||
    !isString(offer.offerId) ||
    offer.bookingMode !== 'prepare' ||
    !isNumber(offer.price) ||
    !isString(offer.currency) ||
    !isString(offer.conditionsSummary) ||
    !Array.isArray(offer.conditions) ||
    !offer.conditions.every(isString)
  ) {
    warnInvalidOffer(choice.choiceId, 'rail');
    return null;
  }
  return {
    kind: 'train',
    id: choice.choiceId,
    artifactId,
    title: choice.choiceTitle ?? 'Train',
    mode: 'instant',
    legs: isRailJourney(offer.journey) ? [legFromJourney(offer.journey)] : [],
    fares: railFares(offer).map((fare) => ({
      id: fare.offerId,
      ...railFareName(fare),
      total: fare.price,
      currency: fare.currency || choice.priceCurrency || 'GBP',
      bags: null,
      conditions: capitalizeConditionSegments(fare.conditionsSummary),
      refundable: null,
    })),
    price: offer.price,
    currency: offer.currency || choice.priceCurrency || 'GBP',
    passengers: 1,
    blurb:
      choice.choiceBlurb ??
      capitalizeConditionSegments(offer.conditionsSummary),
  };
}

export function adaptReturnChoice(
  artifactId: string,
  choice: FlightChoice,
): ReturnFlight | null {
  const offer = choice.flightOffer;
  const slice = offer?.slices.at(-1);
  if (!offer || !slice) return null;
  const leg = legFromSlice(slice, 0, 1);
  const fare = offer.fares?.[0];
  const sourcePrices =
    offer.sources?.flatMap((source) =>
      typeof source.total === 'number'
        ? [{ total: source.total, currency: source.currency }]
        : [],
    ) ?? [];
  const separateTotal =
    offer.separateTickets && sourcePrices.length > 0
      ? sourcePrices.reduce((total, source) => total + source.total, 0)
      : undefined;
  const price =
    choice.fromPrice ??
    fare?.total ??
    separateTotal ??
    offer.sources?.[0]?.total ??
    0;
  const rawNote = (choice as { note?: unknown }).note;
  const note =
    isRecord(rawNote) && isString(rawNote.headline) && rawNote.headline.trim()
      ? {
          headline: rawNote.headline.trim(),
          ...(isString(rawNote.detail) && rawNote.detail.trim()
            ? { detail: rawNote.detail.trim() }
            : {}),
        }
      : undefined;
  return {
    id: choice.choiceId,
    choiceId: choice.choiceId,
    ...(note ? { note } : {}),
    dep: leg.dep,
    arr: leg.arr,
    from: leg.from,
    to: leg.to,
    airlines: Array.from(
      new Set(leg.segments.map((segment) => segment.carrierName)),
    ).join(' + '),
    durationMin: leg.durationMin,
    stops: leg.segments.length - 1,
    price,
    currency:
      choice.priceCurrency ??
      fare?.currency ??
      offer.sources?.[0]?.currency ??
      'GBP',
    leg,
    fares: offer.fares?.map((item) => ({
      id: item.offerId,
      name: item.brandName || item.cabin,
      total: item.total,
      currency: item.currency,
      bags: travelBagsLabel(item),
      conditions: capitalizeConditionSegments(item.conditions),
      refundable: item.refundable,
    })),
    separateTickets: Boolean(offer.separateTickets),
    sourcePrices,
  };
}

export const groupOf = (option: TravelOption): OptionGroup =>
  option.kind === 'hotel' ? 'stays' : 'transport';

export function outboundFares(option: TransportOption): LegFare[] {
  return option.fares.map((fare) => ({
    id: fare.id,
    name: fare.name,
    nameDetail: fare.nameDetail,
    price: fare.total,
    currency: fare.currency,
    bags: fare.bags,
    conditions: fare.conditions,
  }));
}

export function returnFares(returned: ReturnFlight): LegFare[] {
  return (returned.fares ?? []).map((fare) => ({
    id: fare.id,
    name: fare.name,
    nameDetail: fare.nameDetail,
    price: fare.total,
    currency: fare.currency,
    bags: fare.bags,
    conditions: fare.conditions,
  }));
}
